import type { ModuleOutput, ModuleProperty, Shape } from "@/library/typeDefinations";
import { ChannelPort, ChannelPortWithSize, ContentUnit } from "../other";
import { PayloadChannel, PayloadShape } from "../payloadPrimitives";
import { ComponentObject } from "../typeDefinations";
import { Path } from "../primitives";
// DEBUG: locate ModuleOutput definition
type __WhereIsModuleOutput = ModuleOutput;




type ModulePos = [number, number] | number[] | [[number, number]] | number;

const initializeChannelPortsWithoutSize = (m: ModuleTemplate) => {
  for (let i = 0; i < m.numberOfChannelPorts; i++) {
    m.channelPorts.push(new ChannelPort(0, 0, m.id, i));
  }
};

// set size separately in updatePortHelper(), or override this function if size is fixed for all instances
const initializeChannelPortsWithSize = (m: ModuleTemplate) => {
  for (let i = 0; i < m.numberOfChannelPorts; i++) {
    m.channelPorts.push(new ChannelPortWithSize(0, 0, m.id, i, 0, 0));
  }
};

export default abstract class ModuleTemplate extends ContentUnit {
  translate = [0, 0]; // for temporary position
  position: number[];
  rotation: number;
  abstract id: string;

  // properties must contain at least `height`
  abstract properties: ModuleProperty;

  abstract get numberOfChannelPorts(): number;

  get type(): string {
    return "module";
  }

  elementsToRender = {
    topLeftCorner: [0, 0], // for mask boundary
    centerPoint: [0, 0], // as rotation origin
    borderShapes: Array<Shape>(),
    maskShapes: Array<Shape>() // mask elements
  };

  channelPorts = Array<ChannelPort | ChannelPortWithSize>();
  static count = 0;

  constructor(pos: ModulePos, layer: string, rotation = 0) {
    super(layer);

    let x = 0;
    let y = 0;

    // [x, y]
    if (Array.isArray(pos) && typeof pos[0] === "number") {
      x = pos[0];
      y = typeof pos[1] === "number" ? pos[1] : 0;

      // [[x, y]]
    } else if (Array.isArray(pos) && Array.isArray(pos[0])) {
      const inner = pos[0];
      x = typeof inner[0] === "number" ? inner[0] : 0;
      y = typeof inner[1] === "number" ? inner[1] : 0;

      // number
    } else if (typeof pos === "number") {
      x = pos;
      y = 0;
    } else {
      console.warn("[ModuleTemplate] unexpected pos =", pos);
    }

    this.position = [x, y];
    this.rotation = rotation;

    console.log("[ModuleTemplate.constructor] normalized position =", this.position);
  }

  abstract updateRender(): void;
  abstract updatePortHelper(): void;
  abstract clone(): ModuleTemplate;
  abstract setNewModuleId(): void;
  abstract get getModuleIndex(): number;
  abstract get portSizeFixed(): boolean;

  getHeight(): number {
    const h = (this.properties as any)?.height?.value;
    if (typeof h !== "number" || Number.isNaN(h)) {
      const keys = this.properties ? Object.keys(this.properties) : [];
      throw new Error(
        `[ModuleTemplate] Missing/invalid properties.height on ${this.id}. Available keys: ${keys.join(", ")}`
      );
    }
    return h;
  }

  buildOutput(layer: string): ModuleOutput {
    const properties = new Map<string, number>();
    for (const [key, value] of Object.entries(this.properties)) {
      properties.set(key, (value as any).value);
    }

    
    const idx = this.getModuleIndex;
    if (typeof idx !== "number" || Number.isNaN(idx)) {
      throw new Error(`[ModuleTemplate.buildOutput] invalid moduleIdx=${idx} for ${this.id}`);
    }

    
    const meta = this as unknown as { templateId?: string; componentId?: string };

    return {
      id: this.id,
      position: this.position,
      rotation: this.rotation,
      properties: Object.fromEntries(properties),
      moduleIdx: idx,
      layer,

     
      templateId: meta.templateId,
      componentId: meta.componentId
    };
  }

  updatePort(): void {
    if (this.channelPorts.length === this.numberOfChannelPorts) {
      this.updatePortHelper();
    }
  }

  initializeChannelPorts(): void {
    if (this.portSizeFixed) {
      initializeChannelPortsWithSize(this);
    } else {
      initializeChannelPortsWithoutSize(this);
    }
  }

  updatePosition(): void {
    this.position[0] += this.translate[0];
    this.position[1] += this.translate[1];
    this.translate = [0, 0];
    this.updateRender();
  }

  setTranslate(tx: number, ty: number): void {
    // set channel ports accordingly
    this.channelPorts.forEach((p) => {
      p.position[0] += tx - this.translate[0];
      p.position[1] += ty - this.translate[1];
    });
    this.translate[0] = tx;
    this.translate[1] = ty;
  }

  setRotation(r: number): void {
    // counterclockwise rotation
    // make sure translate == [0, 0]
    // set channel ports accordingly
    const radius = ((r - this.rotation) / 180) * Math.PI;
    const cosR = Math.cos(radius);
    const sinR = Math.sin(radius);
    this.channelPorts.forEach((p) => {
      const x0 = p.position[0] - this.elementsToRender.centerPoint[0];
      const y0 = p.position[1] - this.elementsToRender.centerPoint[1];
      const x1 = x0 * cosR - y0 * sinR;
      const y1 = x0 * sinR + y0 * cosR;
      p.position[0] = x1 + this.elementsToRender.centerPoint[0];
      p.position[1] = y1 + this.elementsToRender.centerPoint[1];
    });
    this.rotation = r;
  }

  buildPayload(z: number, hOffset: number): ComponentObject {
    let shapes = Array<PayloadShape>();
    const channels = Array<PayloadChannel>();

    const height = this.getHeight() + hOffset;

    this.elementsToRender.borderShapes.forEach((elm) => {
      if (elm instanceof Path) {
        channels.push(
          elm.buildPayload(z, height, this.translate, this.rotation, this.elementsToRender.centerPoint)
        );
      } else {
        const ss = elm.buildPayload(z, height, this.translate, this.rotation, this.elementsToRender.centerPoint);
        if (ss) shapes = shapes.concat(ss);
      }
    });

    this.elementsToRender.maskShapes.forEach((elm) => {
      if (elm instanceof Path) {
        channels.push(
          elm.buildPayload(z, height, this.translate, this.rotation, this.elementsToRender.centerPoint)
        );
      } else {
        const ss = elm.buildPayload(z, height, this.translate, this.rotation, this.elementsToRender.centerPoint);
        if (ss) shapes = shapes.concat(ss);
      }
    });

    return {
      id: this.id,
      shapes: shapes,
      channels: channels,
      ports: Array<string>()
    };
  }
}
